import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  IconButtonComponent,
  IconComponent,
} from '@totalenergiescode/tds-angular';

@Component({
  selector: 'lib-inline-edit-field',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, IconComponent, IconButtonComponent],
  template: `
    <div class="inline-edit-field">
      @if (!editing()) {
        <span class="inline-edit-field__value">{{ value() }}</span>
        <button
          tds-icon-button
          variant="tertiary"
          size="sm"
          label="Edit"
          aria-label="Edit {{ propertyKey() }}"
          (click)="startEdit()"
        >
          <tds-icon [name]="$any('edit')"></tds-icon>
        </button>
      } @else {
        <input
          #editInput
          class="inline-edit-field__input"
          type="text"
          [(ngModel)]="editValue"
          [disabled]="saving()"
          (keydown.enter)="save()"
          (keydown.escape)="cancel()"
          [attr.aria-label]="'Edit ' + propertyKey()"
        />
        <button
          tds-icon-button
          variant="tertiary"
          size="sm"
          label="Save"
          aria-label="Save {{ propertyKey() }}"
          [disabled]="saving()"
          (click)="save()"
        >
          <tds-icon [name]="$any('check')"></tds-icon>
        </button>
        <button
          tds-icon-button
          variant="tertiary"
          size="sm"
          label="Cancel"
          aria-label="Cancel editing {{ propertyKey() }}"
          [disabled]="saving()"
          (click)="cancel()"
        >
          <tds-icon [name]="$any('close')"></tds-icon>
        </button>
      }
      @if (error()) {
        <span class="inline-edit-field__error" role="alert">
          {{ error() }}
        </span>
      }
    </div>
  `,
  styles: [
    `
      .inline-edit-field {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        flex-wrap: wrap;
      }

      .inline-edit-field__value {
        flex: 1;
        min-width: 0;
      }

      .inline-edit-field__input {
        flex: 1;
        min-width: 0;
        padding: 0.25rem 0.5rem;
        border: 1px solid var(--tds-color-border-default);
        border-radius: var(--tds-border-radius-sm);
        font-family: inherit;
        font-size: inherit;
        line-height: inherit;
      }

      .inline-edit-field__input:focus {
        outline: 2px solid var(--tds-color-primary);
        outline-offset: 2px;
      }

      .inline-edit-field__input:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .inline-edit-field__error {
        width: 100%;
        color: var(--tds-color-error);
        font-size: 0.875rem;
      }
    `,
  ],
})
export class InlineEditFieldComponent {
  readonly value = input.required<string>();
  readonly propertyKey = input.required<string>();

  readonly saved = output<{ propertyName: string; value: string }>();

  readonly editing = signal(false);
  readonly editValue = signal('');
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);

  private readonly _resetOnValueChange = effect(() => {
    const val = this.value();
    untracked(() => {
      this.editValue.set(val);
      this.editing.set(false);
      this.saving.set(false);
      this.error.set(null);
    });
  });

  startEdit(): void {
    this.editValue.set(this.value());
    this.editing.set(true);
    this.error.set(null);

    // Focus the input after the view updates
    setTimeout(() => {
      const input = document.querySelector(
        '.inline-edit-field__input',
      ) as HTMLInputElement;
      input?.focus();
    }, 0);
  }

  cancel(): void {
    this.editing.set(false);
    this.editValue.set(this.value());
    this.error.set(null);
  }

  save(): void {
    if (this.saving()) return;

    const newValue = this.editValue().trim();
    if (newValue === this.value()) {
      this.cancel();
      return;
    }

    this.error.set(null);
    this.saving.set(true);

    this.saved.emit({
      propertyName: this.propertyKey(),
      value: newValue,
    });
  }

  // Public method to be called by parent after save completes
  completeSave(success: boolean, errorMessage?: string): void {
    this.saving.set(false);
    if (success) {
      this.editing.set(false);
      this.error.set(null);
    } else {
      this.error.set(errorMessage || 'Failed to save');
    }
  }
}
